import React, { useState } from 'react';
import { Stack, Typography, IconButton, Avatar, Divider, Button, Chip, Box } from '@mui/material';
import { ChevronLeft, Close } from '@mui/icons-material';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { PurchaseOrderDetails, useProduct } from '@features/index';
import { useProductInventory } from '@features/inventory/hooks/useInventory';
import { PurchaseOrder } from '@domain/purchasing';
import { useNavigate } from 'react-router-dom';
import {
  ProductInventoryPreview,
  ProductDetailsSkeleton,
  ProductEmptyState,
  ProductInfoHeader,
  ProductForecastBlock,
  ProductOrderingSettings,
  ProductPurchaseOrderList,
  ProductSuppliersBlock,
} from '.';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { IconSize, Icon } from '@shared/icons';
import { useToast } from '@shared/hooks/useToast';

interface ProductDetailsDrawerProps {
  productId: string;
}

const ProductDetailsDrawer: React.FC<ProductDetailsDrawerProps> = ({ productId }) => {
  const { closeDrawer, openDrawer, onBack, onClose } = useGenericDrawer();
  const { product, isLoading } = useProduct(productId);
  const { inventory } = useProductInventory(productId);
  const toast = useToast();
  const [showFullDescription, setShowFullDescription] = useState(false);

  const navigate = useNavigate();

  const handlePurchaseOrderItemClick = (purchaseOrder: PurchaseOrder) => {
    if (purchaseOrder.id) {
      openDrawer(
        'Purchase Order Details',
        <PurchaseOrderDetails purchaseOrderId={purchaseOrder.id} />,
        closeDrawer
      );
    }
  };

  const handleViewInTimeTravel = () => {
    if (product?.id) {
      navigate(`/timetravel/products/${productId}`);
      closeDrawer();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <>
      <Stack
        direction="row"
        position="sticky"
        top={0}
        p={4}
        zIndex={1}
        bgcolor="background.paper"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" alignItems="center" spacing={0}>
          {onBack && (
            <IconButton onClick={onBack} edge="start">
              <ChevronLeft />
            </IconButton>
          )}
          <Typography variant="h6">Product Details</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {onClose && (
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          )}
        </Stack>
      </Stack>


      {isLoading ? (
        <ProductDetailsSkeleton />
      ) : !product ? (
        <ProductEmptyState />
      ) : (
        <Stack gap={3} sx={{ flexGrow: 1, p: 4, pt: 0 }}>

          <Stack direction="row" spacing={3}>
            <Avatar variant="rounded" src={product.image} sx={{ width: 56, height: 56, border: 1, borderColor: 'divider' }} />
            <Typography variant="body1">{product.title}</Typography>
          </Stack>

          <Stack direction="row" spacing={3}>
            {product.ean && (
              <Chip label={`EAN: ${product.ean}`} onClick={() => copyToClipboard(product.ean)} />
            )}
            {product.sku && (
              <Chip label={`SKU: ${product.sku || 'N/A'}`} onClick={() => copyToClipboard(product.sku || '')} />
            )}
            {product.customFields && Object.entries(product.customFields).map(([key, value]) => (
              <Chip label={`${key}: ${value || 'N/A'}`} onClick={() => copyToClipboard(value || '')} />
            ))}
          </Stack>


          {product.description && (
            <Stack direction="column" spacing={3}>
              <Typography variant="body1"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: showFullDescription ? 'none' : 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
                onClick={() => setShowFullDescription(!showFullDescription)}>
                <span dangerouslySetInnerHTML={{ __html: product.description }} />
              </Typography>

              <Button size="small" onClick={() => setShowFullDescription(!showFullDescription)}>
                {showFullDescription ? 'Show Less' : 'Show More'}
              </Button>
            </Stack>
          )}

          <Divider />

          <ProductOrderingSettings product={product} />

          <ProductForecastBlock
            productId={productId}
            forecastGradeFallback={product.forecastGrade}
            onViewInTimeTravel={handleViewInTimeTravel}
          />

          <ProductInventoryPreview inventory={inventory} />

          <ProductSuppliersBlock product={product} />

          <ProductPurchaseOrderList
            product={product}
            onItemClick={handlePurchaseOrderItemClick}
          />

        </Stack >
      )}

    </>
  );
};

export default ProductDetailsDrawer;
